import axios from 'axios';
import { ensureAdminRequestHeaders } from '../auth/adminRequestHeaders';
import { getAdminPaymentRequestOptions } from '../payment/adminPaymentRequestConfig';
import { daonResponseRecord, invalidDaonResponse, parseDaonAdjustment, parseDaonBalance, parseDaonInsiders, parseDaonOverview, parseDaonPolicy, parseDaonPreview, type DaonAdjustmentInput, type DaonPolicy } from './daonAdminModel';
export * from './daonAdminModel';

const API = '/api/v1/admin/iap/saju-ai';
const INSIDER_API = '/api/admin/internal-testers';

async function config(token: string | null | undefined, signal?: AbortSignal) {
  return { ...getAdminPaymentRequestOptions(), headers: await ensureAdminRequestHeaders(token), withCredentials: true, signal };
}
async function insiderConfig(token: string | null | undefined, signal?: AbortSignal) {
  return { headers: await ensureAdminRequestHeaders(token), withCredentials: true, signal };
}
export async function getDaonOverview(token: string | null | undefined, signal?: AbortSignal) {
  return parseDaonOverview((await axios.get<unknown>(`${API}/overview`, await config(token, signal))).data);
}
export async function getDaonInsiders(token: string | null | undefined, signal?: AbortSignal) {
  return parseDaonInsiders((await axios.get<unknown>(INSIDER_API, await insiderConfig(token, signal))).data);
}
export async function addDaonInsider(token: string | null | undefined, email: string) {
  await axios.post(INSIDER_API, { email: email.trim().toLowerCase() }, await insiderConfig(token));
}
export async function removeDaonInsider(token: string | null | undefined, email: string) {
  await axios.delete(`${INSIDER_API}/${encodeURIComponent(email.trim().toLowerCase())}`, await insiderConfig(token));
}
export async function saveDaonPolicy(token: string | null | undefined, input: Omit<DaonPolicy, 'revision'> & { expectedRevision: number; reason: string }) {
  return parseDaonPolicy((await axios.put<unknown>(`${API}/policy`, input, await config(token))).data);
}
export async function getDaonBalance(token: string | null | undefined, userId: string, signal?: AbortSignal) {
  return parseDaonBalance((await axios.get<unknown>(`${API}/users/${encodeURIComponent(userId)}`, await config(token, signal))).data);
}
export async function getDaonAdjustments(token: string | null | undefined, userId: string, signal?: AbortSignal) {
  const rows = (await axios.get<unknown>(`${API}/users/${encodeURIComponent(userId)}/adjustments`, await config(token, signal))).data;
  if (!Array.isArray(rows)) return invalidDaonResponse();
  return rows.map(parseDaonAdjustment);
}
export async function previewDaonAdjustment(token: string | null | undefined, userId: string, input: DaonAdjustmentInput) {
  return parseDaonPreview((await axios.post<unknown>(`${API}/users/${encodeURIComponent(userId)}/adjustments/preview`, input, await config(token))).data);
}
export async function applyDaonAdjustment(token: string | null | undefined, userId: string, input: DaonAdjustmentInput) {
  const row = daonResponseRecord((await axios.post<unknown>(`${API}/users/${encodeURIComponent(userId)}/adjustments`, input, await config(token))).data);
  if (typeof row['idempotentReplay'] !== 'boolean') return invalidDaonResponse();
  return { adjustment: parseDaonAdjustment(row['adjustment']), balance: parseDaonBalance(row['balance']), idempotentReplay: row['idempotentReplay'] };
}
